import { useCallback, useEffect, useState } from "react";
import ButtonBase from "@mui/material/ButtonBase";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import CheckIcon from "@mui/icons-material/Check";
import type { Currency, Lang } from "../types";
import { useLang } from "../i18n/LangContext";
import { useAppState } from "../state/AppState";

const LANGS: { value: Lang; label: string }[] = [
  { value: "de", label: "Deutsch" },
  { value: "en", label: "English" },
  { value: "is", label: "Íslenska" },
];
const CURRENCIES: Currency[] = ["ISK", "EUR", "USD", "CHF", "GBP"];

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

interface SettingsMenuProps {
  compact?: boolean;
}

export function SettingsMenu({ compact = false }: SettingsMenuProps) {
  const { lang, setLang, t } = useLang();
  const { currency, setCurrency } = useAppState();
  const [menuEl, setMenuEl] = useState<null | HTMLElement>(null);
  const [installEvent, setInstallEvent] = useState<InstallPromptEvent | null>(null);

  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setInstallEvent(e as InstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const openMenu = useCallback(
    (e: React.MouseEvent<HTMLElement>) => setMenuEl(e.currentTarget),
    [],
  );
  const closeMenu = useCallback(() => setMenuEl(null), []);

  const triggerInstall = async () => {
    if (!installEvent) return;
    await installEvent.prompt();
    setInstallEvent(null);
    closeMenu();
  };

  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches ||
      (window.navigator as { standalone?: boolean }).standalone === true);
  const showInstall =
    typeof navigator !== "undefined" &&
    !isStandalone &&
    (installEvent || /iPhone|iPad/.test(navigator.userAgent));

  return (
    <>
      <ButtonBase
        onClick={openMenu}
        aria-label={t("settings")}
        aria-haspopup="menu"
        aria-expanded={!!menuEl}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          px: 1,
          py: 0.5,
          borderRadius: 1,
          color: "text.secondary",
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: 0.4,
          "&:hover": { color: "text.primary", bgcolor: "action.hover" },
        }}
      >
        <span>
          {lang.toUpperCase()}
          {!compact && (
            <>
              <Box component="span" sx={{ opacity: 0.5, mx: 0.5 }}>|</Box>
              {currency}
            </>
          )}
        </span>
        <SettingsOutlinedIcon fontSize="small" />
      </ButtonBase>
      <Menu
        anchorEl={menuEl}
        open={!!menuEl}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { minWidth: 180 } } }}
        MenuListProps={{ dense: true }}
      >
        <Box
          sx={{
            px: 2,
            pt: 1,
            pb: 0.25,
            fontSize: 10,
            color: "text.disabled",
            letterSpacing: 0.6,
            textTransform: "uppercase",
          }}
        >
          {t("language")}
        </Box>
        {LANGS.map(({ value, label }) => (
          <MenuItem
            key={value}
            dense
            selected={lang === value}
            onClick={() => {
              setLang(value);
              closeMenu();
            }}
          >
            <ListItemText>{label}</ListItemText>
            {lang === value && <CheckIcon fontSize="small" sx={{ ml: 1, opacity: 0.7 }} />}
          </MenuItem>
        ))}
        <Divider sx={{ my: 0.5 }} />
        <Box
          sx={{
            px: 2,
            pt: 1,
            pb: 0.25,
            fontSize: 10,
            color: "text.disabled",
            letterSpacing: 0.6,
            textTransform: "uppercase",
          }}
        >
          {t("currency")}
        </Box>
        {CURRENCIES.map((c) => (
          <MenuItem
            key={c}
            dense
            selected={currency === c}
            onClick={() => {
              setCurrency(c);
              closeMenu();
            }}
          >
            <ListItemText>{c}</ListItemText>
            {currency === c && <CheckIcon fontSize="small" sx={{ ml: 1, opacity: 0.7 }} />}
          </MenuItem>
        ))}
        {showInstall && (
          <>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem dense onClick={installEvent ? triggerInstall : closeMenu}>
              <ListItemText
                primary={t("installApp")}
                secondary={installEvent ? undefined : t("installHintIos")}
                secondaryTypographyProps={{ sx: { fontSize: 11 } }}
              />
            </MenuItem>
          </>
        )}
      </Menu>
    </>
  );
}
